import { isNil } from 'lodash';
import { LatLng } from '../../components/Map/Map';
import { AddressParts } from '../../hooks/useDesktopGoogleApi';
import { AddressNoteForm } from '../../hooks/validations/useAddressNoteValidationForm';
import { ReceptionOpeningHours, WeekDay } from '../../models';
import { Address, TimeInterval } from '../../models/address.models';

type AddressNoteIntervals = {
  receptionMorningFromHour: string;
  receptionMorningToHour: string;
  receptionAfternoonFromHour: string;
  receptionAfternoonToHour: string;
};

export function buildAddressFromForms(
  addressId: number | undefined,
  address: AddressParts | undefined,
  customCoordinates: LatLng | undefined,
  notes: AddressNoteForm | undefined,
  receptionDays: ReceptionOpeningHours | undefined,
  addressName: string | undefined
): any {
  return {
    id: addressId,
    city: address?.aal3,
    country: address?.country,
    doorbellLatitude: customCoordinates?.lat,
    doorbellLongitude: customCoordinates?.lng,
    doorbell:
      !notes || notes?.intercomName.length === 0 ? null : notes?.intercomName,
    elevator: notes?.hasElevator,
    floorNumber: (notes && +notes.floor) || undefined,
    instructions: notes && notes.notes.length > 0 ? notes?.notes : undefined,
    lat: address?.lat,
    lon: address?.lng,
    number: address?.streetNumber,
    route: address?.street,
    reception: notes?.isReception ? receptionDays : undefined,
    // premiseType: notes?.premiseType,
    addressType: notes?.addressType,
    zipCode: address?.zipCode,
    address: address?.formattedAddress,
    name: addressName || notes?.addressName,
  };
}

export type BuildReceptionDaysParams = {
  isReception?: boolean;
  receptionAfternoonFromHour: string;
  receptionMorningToHour: string;
  receptionAfternoonToHour: string;
  receptionMorningFromHour: string;
  days?: WeekDay[];
};

export function buildReceptionDays({
  isReception = true,
  receptionAfternoonFromHour,
  receptionMorningToHour,
  receptionAfternoonToHour,
  receptionMorningFromHour,
  days,
}: BuildReceptionDaysParams) {
  if (!isReception) return undefined;

  const receptionHours: [TimeInterval, TimeInterval] = [
    {
      from: `${receptionMorningFromHour}:00`,
      to: `${receptionMorningToHour}:00`,
    },
    {
      from: `${receptionAfternoonFromHour}:00`,
      to: `${receptionAfternoonToHour}:00`,
    },
  ];

  const receptionDays: ReceptionOpeningHours = {
    monday: findDay(days, WeekDay.Mon) ? receptionHours : [],
    thursday: findDay(days, WeekDay.Thu) ? receptionHours : [],
    wednesday: findDay(days, WeekDay.Wed) ? receptionHours : [],
    tuesday: findDay(days, WeekDay.Tue) ? receptionHours : [],
    friday: findDay(days, WeekDay.Fri) ? receptionHours : [],
    saturday: findDay(days, WeekDay.Sat) ? receptionHours : [],
    sunday: [],
  };
  return receptionDays;
}

function findDay(days: WeekDay[] | undefined, day: WeekDay) {
  if (days) {
    return days?.includes(day);
  } else return false;
}

export const transformAddressInEditAddressModels = (address: Address) => {
  const addressParts: AddressParts = getAddressPartsFromAddress(address);
  const addressNotes: AddressNoteForm = getAddressNotesFromAddress(address);
  const customCoordinates: LatLng = getCustomCoordinatesFromAddress(address);

  return {
    addressNotes,
    addressParts,
    customCoordinates,
  };
};

function getCustomCoordinatesFromAddress(address: Address): LatLng {
  return {
    lat: address.doorbellLatitude,
    lng: address.doorbellLongitude,
  };
}

function getAddressNotesFromAddress(address: Address) {
  const isReception = getIsReception(address);
  const days: WeekDay[] | undefined = getWeekDays(address);
  const receptionTimeIntervals = getReceptionTimeIntervals(address);

  const addressNotes: AddressNoteForm = {
    addressName: address.name,
    floor: (address.floorNumber && +address.floorNumber) || 0,
    hasElevator: !!address.elevator,
    intercomName: address.doorbell || '',
    isReception: isReception,
    isSavingAddress: true,
    notes: address.instructions || '',
    // premiseType: address.premiseType,
    addressType: address.addressType,
    days: days,
    receptionAfternoonFromHour:
      receptionTimeIntervals?.receptionAfternoonFromHour || '14',
    receptionAfternoonToHour:
      receptionTimeIntervals?.receptionAfternoonToHour || '18',
    receptionMorningFromHour:
      receptionTimeIntervals?.receptionMorningFromHour || '08',
    receptionMorningToHour:
      receptionTimeIntervals?.receptionMorningToHour || '13',
  };
  return addressNotes;
}

function getAddressPartsFromAddress(address: Address): AddressParts {
  return {
    streetNumber: address.number,
    street: address.route,
    country: address.country,
    lat: address.lat,
    lng: address.lon,
    zipCode: address.zipCode,
    formattedAddress: address.address,
    aal1: address.country,
    aal2: address.country,
    aal3: address.city,
  };
}

function getIsReception(address: Address) {
  return isNil(address.reception)
    ? false
    : !!Object.keys(address.reception).find(
        (k) =>
          address!.reception![k as WeekDay] &&
          address!.reception![k as WeekDay]!.length > 0
      );
}

function getWeekDays(address: Address): WeekDay[] | undefined {
  if (isNil(address.reception)) return undefined;

  return Object.keys(address.reception).filter((w) => {
    const weekDay = w as WeekDay;
    const timeSlots = address.reception![weekDay];
    return timeSlots && timeSlots.length > 0;
  }) as WeekDay[];
}

function getReceptionTimeIntervals(
  address: Address
): Partial<AddressNoteIntervals> | undefined {
  if (isNil(address.reception)) return undefined;

  const dayWithTimeSlot: WeekDay | undefined = Object.keys(
    address.reception
  ).find((w) => {
    const weekDay = w as WeekDay;
    const timeSlots = address.reception![weekDay];
    return timeSlots && timeSlots.length > 0;
  }) as WeekDay;

  if (isNil(dayWithTimeSlot)) return undefined;

  const noteIntervals: Partial<AddressNoteIntervals> = {};
  const timeIntervals = address.reception[dayWithTimeSlot];

  timeIntervals?.forEach((ti) => {
    const fromAsNumber = +getHoursFromString(ti.from);

    if (fromAsNumber < 14) {
      noteIntervals.receptionMorningFromHour = getHoursFromString(ti.from);
      noteIntervals.receptionMorningToHour = getHoursFromString(ti.to);
    } else {
      noteIntervals.receptionAfternoonFromHour = getHoursFromString(ti.from);
      noteIntervals.receptionAfternoonToHour = getHoursFromString(ti.to);
    }
  });

  return noteIntervals;
}

function getHoursFromString(s: string) {
  return s.split(':')[0];
}
